import { DeferredSNSMessage, SnsMessage } from '@tm/types/event/sns'
import { useCallback, useContext, useEffect } from 'react'
import { WebsocketContext } from './context'

type IncomingMessage = SnsMessage | DeferredSNSMessage

type ExtractSnsMessage<T extends SnsMessage['topic']> = Extract<SnsMessage, { topic: T }>

const fetchDeferredMessage = async (deferred: DeferredSNSMessage) => {
  const response = await fetch(deferred.$s3, { method: 'GET' })
  return response.json() as Promise<SnsMessage>
}

export const useWebsocketEvent = <T extends SnsMessage['topic']>(
  eventName: T,
  callback: (messageBody: ExtractSnsMessage<T>) => void
) => {
  const { websocket } = useContext(WebsocketContext)

  const handleWebsocketEvent = useCallback(
    async (event: MessageEvent) => {
      try {
        let messageBody = JSON.parse(event.data as string) as IncomingMessage

        // Message deferred to S3?
        if ('$s3' in messageBody) {
          messageBody = await fetchDeferredMessage(messageBody)
        }

        if (messageBody.topic === eventName) callback(messageBody as ExtractSnsMessage<T>)
      } catch (e) {
        console.log(e)
      }
    },
    [callback, eventName]
  )

  const onWebsocketMessage = useCallback(
    (event: MessageEvent) => {
      void handleWebsocketEvent(event)
    },
    [handleWebsocketEvent]
  )

  useEffect(() => {
    if (websocket) {
      websocket.addEventListener('message', onWebsocketMessage)
      return () => {
        websocket.removeEventListener('message', onWebsocketMessage)
      }
    }
  }, [onWebsocketMessage, websocket])
}
